import React from 'react';
import type { EscalationEvent } from '../../types/escalationRuntime.types';
import type { NotificationHistoryEvent } from '../../types/criticalNotification.types';
import { simplifyEscalationEventMessage } from '../../utils/criticalNotificationUtils';

type TimelineEvent = EscalationEvent | NotificationHistoryEvent;

interface EscalationTimelineProps {
  events: TimelineEvent[];
  emptyMessage?: string;
}

function formatTimestamp(value?: string | null) {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toLocaleString();
}

function formatEventType(eventType: string) {
  return eventType.replace(/([A-Z])/g, ' $1').trim() || 'Event';
}

function getRecipientName(event: TimelineEvent): string | null {
  if ('recipientName' in event && event.recipientName) {
    return event.recipientName;
  }
  return null;
}

function getRecipientRole(event: TimelineEvent): string | null {
  if (event.role) {
    return event.role;
  }
  if ('recipientRole' in event && event.recipientRole) {
    return event.recipientRole;
  }
  return null;
}

export default function EscalationTimeline({
  events,
  emptyMessage = 'No escalation events recorded yet.',
}: Readonly<EscalationTimelineProps>) {
  if (!Array.isArray(events) || events.length === 0) {
    return <p className="esc-monitor-placeholder">{emptyMessage}</p>;
  }

  return (
    <ol
      className="esc-monitor-timeline"
      aria-label="Event timeline"
    >
      {events.map((event, index) => {
        const eventType = String(event.eventType || 'Event');
        const role = getRecipientRole(event);
        const recipientName = getRecipientName(event);
        const displayStatus =
          'displayStatus' in event && event.displayStatus ? String(event.displayStatus) : null;
        const dotClass = eventType.toLowerCase().replace(/[^a-z0-9_-]/g, '') || 'event';

        return (
          <li
            key={`${String(event.id)}-${index}`}
            className="esc-monitor-timeline-item"
          >
            <div className={`esc-monitor-timeline-dot esc-monitor-timeline-dot--${dotClass}`} />
            <div>
              <p className="esc-monitor-timeline-title">
                {formatEventType(eventType)}
                {displayStatus && displayStatus !== eventType ? ` · ${displayStatus}` : ''}
                {event.levelNumber ? ` · Level ${event.levelNumber}` : ''}
                {role ? ` · ${role}` : ''}
                {recipientName ? ` · ${recipientName}` : ''}
              </p>
              <p className="esc-monitor-timeline-message">
                {simplifyEscalationEventMessage(event.message)}
              </p>
              <p className="esc-monitor-timeline-time">{formatTimestamp(event.createdAt)}</p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
